// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Carbon display for the SOP Factory Catalog. Fetches SOP artifacts
// (GET /api/v1/sop-factory/artifacts?artifact_type=sop) and renders them in a
// Carbon DataTable. Rows are clickable and open the standalone SOP detail page.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  Table,
  TableContainer,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import { listSops } from '../../services/sopFactoryService';
import { EmptyState } from '../shell/ScreenHelpers';
import type { ArtifactStatus } from '../../types';

/** SOP status → Helios theme color token + label. Rendered as a themed span pill
 * (like SopArtifactDetail's StatusPill / ScreenHelpers' SevTag) so it tracks the
 * active g100/g10 theme instead of relying on Carbon's Tag surface, which reads
 * as light in hs-page content. */
const STATUS_TAG: Record<string, { color: string; label: string }> = {
  approved: { color: 'var(--green)', label: 'Approved' },
  in_review: { color: 'var(--blue)', label: 'In review' },
  draft: { color: 'var(--helios)', label: 'Draft' },
  deprecated: { color: 'var(--text-helper)', label: 'Deprecated' },
};

export function SopStatusTag({ status }: { status: ArtifactStatus | undefined }) {
  if (!status) return <>—</>;
  const cfg = STATUS_TAG[status] ?? { color: 'var(--text-helper)', label: status };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 8px',
        border: '1px solid',
        borderColor: cfg.color,
        borderRadius: 12,
        fontSize: 11,
        lineHeight: 1.6,
        background: 'transparent',
        color: cfg.color,
      }}
    >
      {cfg.label}
    </span>
  );
}

const HEADERS = [
  { key: 'title', header: 'SOP' },
  { key: 'domain', header: 'Domain' },
  { key: 'equipment_type', header: 'Equipment type' },
  { key: 'procedure_type', header: 'Procedure' },
  { key: 'status', header: 'Status' },
];

interface SopRow {
  id: string;
  title: string;
  domain: string;
  equipment_type: string;
  procedure_type: string;
  status: string;
}

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; rows: SopRow[] };

export default function SopFactoryTable({ pageSize = 100 }: { pageSize?: number }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    listSops({ pageSize })
      .then(page => {
        if (cancelled) return;
        const rows: SopRow[] = page.items.map(item => ({
          id: item.artifact_id,
          title: item.title ?? item.artifact_id,
          domain: item.categories?.domain ?? '—',
          equipment_type: item.categories?.equipment_type ?? '—',
          procedure_type: item.categories?.procedure_type ?? '—',
          status: item.status ?? '',
        }));
        setState({ status: 'ok', rows });
      })
      .catch(err => {
        if (!cancelled) {
          setState({ status: 'error', message: (err as Error).message ?? 'Request failed' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pageSize]);

  if (state.status === 'loading') {
    return <DataTableSkeleton headers={HEADERS} columnCount={HEADERS.length} rowCount={5} />;
  }

  if (state.status === 'error') {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Couldn't load SOPs"
        subtitle={state.message}
        hideCloseButton
      />
    );
  }

  if (state.rows.length === 0) {
    return <EmptyState big="No SOPs found." />;
  }

  return (
    <DataTable rows={state.rows} headers={HEADERS} isSortable>
      {({ rows, headers, getHeaderProps, getRowProps, getTableProps }: any) => (
        <TableContainer
          title="Standard Operating Procedures"
          description={`${state.rows.length} published`}
        >
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header: any) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any) => (
                <TableRow
                  {...getRowProps({ row })}
                  key={row.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sops/${row.id}`)}
                >
                  {row.cells.map((cell: any) => (
                    <TableCell key={cell.id}>
                      {cell.info.header === 'status' ? (
                        <SopStatusTag status={(cell.value || undefined) as ArtifactStatus} />
                      ) : (
                        cell.value
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
  );
}
